import { useEffect, useState } from "react";
import { Pencil, Loader2, Eye, EyeOff, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import AdminGate from "@/components/AdminGate";
import PageHeader from "@/components/PageHeader";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
  { key: "platter", label: "Platters", note: "Booked parties only · 4 days' notice" },
  { key: "feast", label: "Kids Feasts", note: "Booked parties only · 2 days' notice" },
  { key: "combo", label: "Party Pack Combos", note: "Booked parties only · 2 days' notice" },
];

export default function AdminMenu() {
  useSeo({ title: "Admin · Menu Manager | Cool Runnings Cape Town", noindex: true });
  return (
    <AdminGate>
      <MenuManager />
    </AdminGate>
  );
}

function MenuManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", price: "", linesText: "", halaal: "", visible: true });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/menu`, { credentials: "include" });
      if (res.ok) setItems(await res.json());
      else toast.error("Could not load the menu");
    } catch {
      toast.error("Could not load the menu");
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      price: item.price || "",
      linesText: (item.lines || []).join("\n"),
      halaal: item.halaal || "",
      visible: item.visible !== false,
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/menu/${editing.id}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          price: form.price,
          lines: form.linesText.split("\n").map((s) => s.trim()).filter(Boolean),
          halaal: form.halaal,
          visible: form.visible,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not save");
      toast.success("Menu updated — live on the site");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="pb-24 pt-10 sm:pt-14" data-testid="menu-admin-page">
      <PageHeader
        tight
        pageName="Admin"
        eyebrow="Staff portal · food"
        title="MENU MANAGER"
        subline="Edit platters, kids feasts and party pack combos — changes appear on the public menu instantly."
        image="/admin-header.jpg"
      />
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {!loaded && <p className="card-dark mt-10 p-6 text-sm text-[#b9b4a6]">Loading the menu…</p>}

        {loaded &&
          CATEGORIES.map((cat) => {
            const group = items.filter((i) => i.category === cat.key);
            return (
              <div key={cat.key} className="mt-10 first:mt-10" data-testid={`menu-admin-group-${cat.key}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-display flex items-center gap-3 text-2xl text-[#f8b818] sm:text-3xl">
                    <UtensilsCrossed size={22} /> {cat.label}
                  </h2>
                  <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#b9b4a6]">{cat.note}</span>
                </div>
                <div className="mt-4 space-y-3">
                  {group.map((item) => (
                    <div
                      key={item.id}
                      className={`card-dark flex flex-wrap items-center justify-between gap-4 p-5 ${item.visible === false ? "opacity-50" : ""}`}
                      data-testid={`menu-admin-item-${item.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-base font-extrabold text-black">
                          {item.name} <span className="font-display ml-2 text-xl text-[#f8b818]">{item.price}</span>
                        </p>
                        <p className="mt-1 truncate text-xs text-black">
                          {(item.lines || []).join(" · ")}
                          {item.halaal ? ` — ${item.halaal}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="btn-outline !px-3 !py-1.5 !text-[10px]"
                          data-testid={`menu-edit-btn-${item.id}`}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={async () => {
                            await fetch(`${API}/admin/menu/${item.id}`, {
                              method: "POST",
                              credentials: "include",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({ visible: item.visible === false }),
                            });
                            load();
                          }}
                          className="rounded-full border border-[#241c0d]/15 p-2 text-[#241c0d]/60 transition-colors hover:border-[#c87700] hover:text-[#c87700]"
                          aria-label={item.visible === false ? "Show on menu" : "Hide from menu"}
                          data-testid={`menu-visible-btn-${item.id}`}
                        >
                          {item.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="!bg-[#fff7e8] !text-[#241c0d]" data-testid="menu-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#241c0d]">EDIT {editing?.name?.toUpperCase()}</DialogTitle>
            <DialogDescription className="!text-[#8a7b5e]">
              Description lines: one item per line (what's included).
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
              <label className="block">
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">Name</span>
                <input
                  className="mt-1 w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-bold text-[#241c0d] outline-none focus:border-[#f8b818]"
                  maxLength={80}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="menu-edit-name-input"
                />
              </label>
              <label className="block">
                <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">Price</span>
                <input
                  className="mt-1 w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-bold text-[#241c0d] outline-none focus:border-[#f8b818]"
                  maxLength={20}
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  data-testid="menu-edit-price-input"
                />
              </label>
            </div>
            <label className="block">
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">Description (one line each)</span>
              <textarea
                className="mt-1 w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-bold text-[#241c0d] outline-none focus:border-[#f8b818]"
                rows={5}
                value={form.linesText}
                onChange={(e) => setForm({ ...form, linesText: e.target.value })}
                data-testid="menu-edit-lines-input"
              />
            </label>
            <label className="block">
              <span className="text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">Halaal note (leave empty for none)</span>
              <input
                className="mt-1 w-full rounded-xl border border-black/15 bg-white px-4 py-2.5 text-sm font-bold text-[#241c0d] outline-none focus:border-[#f8b818]"
                maxLength={120}
                placeholder="e.g. Can be ordered as halaal on request"
                value={form.halaal}
                onChange={(e) => setForm({ ...form, halaal: e.target.value })}
                data-testid="menu-edit-halaal-input"
              />
            </label>
            <label className="flex items-center gap-2.5 text-sm font-extrabold text-[#241c0d]">
              <input
                type="checkbox"
                checked={form.visible}
                onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                className="h-4 w-4 accent-[#f8b818]"
                data-testid="menu-edit-visible-check"
              />
              Show on the public menu
            </label>
          </div>
          <button onClick={save} disabled={busy} className="btn-gold mt-2 w-full justify-center" data-testid="menu-edit-save-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Pencil size={14} />}
            {busy ? "Saving…" : "Save changes"}
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
}
